import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw, Eye } from 'lucide-react';
import apiClient from '@/utils/api';

// Live thumbnail of the CV as it will look in the PDF (re-renders ~1.5s after the CV changes)
export const CVPreviewThumb = ({ cv, lang = 'pt', onOpen }) => {
  const { t } = useTranslation();
  const [src, setSrc] = useState(null);
  const [state, setState] = useState('loading');
  const [tick, setTick] = useState(0);
  const timer = useRef(null);
  const key = JSON.stringify(cv || {});

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setTick((n) => n + 1), 1500);
    return () => clearTimeout(timer.current);
  }, [key, lang]);

  useEffect(() => {
    if (tick === 0) return undefined;
    let cancelled = false;
    let url;
    setState('loading');
    apiClient.get('/cv/me/preview.jpg', { params: { lang, t: Date.now() }, responseType: 'blob', timeout: 60000 })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setSrc((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
        setState('ok');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [tick, lang]);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="cv-preview-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{t('cvBuilder.review.previewTitle')}</p>
        <button type="button" onClick={() => setTick((n) => n + 1)} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="cv-preview-refresh">
          <RefreshCw className={`h-3 w-3 ${state === 'loading' ? 'animate-spin' : ''}`} />{t('cvBuilder.review.previewRefresh')}
        </button>
      </div>
      <button type="button" onClick={onOpen} className="relative block w-full aspect-[210/297] max-h-[520px] mx-auto overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-slate-200 hover:ring-blue-400 transition-shadow" data-testid="cv-preview-open" aria-label={t('cvBuilder.review.previewOpen')}>
        {src && <img src={src} alt={t('cvBuilder.review.previewTitle')} className={`w-full h-full object-cover object-top transition-opacity ${state === 'loading' ? 'opacity-50' : 'opacity-100'}`} data-testid="cv-preview-img" />}
        {state === 'loading' && (
          <span className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs gap-2" data-testid="cv-preview-loading"><Loader2 className="h-5 w-5 animate-spin" />{t('cvBuilder.review.previewLoading')}</span>
        )}
        {state === 'error' && !src && (
          <span className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs p-4 text-center" data-testid="cv-preview-error">{t('cvBuilder.review.previewError')}</span>
        )}
      </button>
      <p className="mt-2 text-[11px] text-slate-500 text-center">{t('cvBuilder.review.previewHint')}</p>
    </div>
  );
};
